import { useCallback } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  autologinRequest,
  clearError,
  confirmRegistrationRequest,
  confirmResetPasswordRequest,
  hideUploadVerificationDocumentsModal,
  loginByVerificationCodeRequest,
  loginRequest,
  logoutRequest,
  resetPasswordRequest,
  showUploadVerificationDocumentsModal,
  uploadVerificationDocumentsRequest,
} from 'features/auth/redux/actions';
import { useSelector } from 'store';

const useAuth = () => {
  const dispatch = useDispatch();
  const { isLoading, isAuthReady, error, isFirstLogin, isUploadDocsVerificationShown, isReUploadDocs } = useSelector(
    state => state.auth,
    shallowEqual
  );
  const { user } = useSelector(state => state.user);

  const onLogin = useCallback(
    (email: string, password: string, successCallback: () => void) =>
      dispatch(loginRequest(email, password, successCallback)),
    [dispatch]
  );

  const onEnterVerificationCode = useCallback(
    (code: string) => dispatch(loginByVerificationCodeRequest(code)),
    [dispatch]
  );

  const onAutoLogin = useCallback((token: string) => dispatch(autologinRequest(token)), [dispatch]);

  const onLogout = useCallback(() => dispatch(logoutRequest()), [dispatch]);

  const onResetPassword = useCallback((email: string) => dispatch(resetPasswordRequest(email)), [dispatch]);

  const onConfirmResetPassword = useCallback(
    (token: string, new_password: string) => {
      dispatch(confirmResetPasswordRequest(token, new_password));
    },
    [dispatch]
  );

  const onConfirmRegistration = useCallback(
    (token: string, new_password: string) => {
      dispatch(confirmRegistrationRequest(token, new_password));
    },
    [dispatch]
  );

  const onUploadVerificationDocuments = useCallback(
    (personal_id: File, trade_license: File) => {
      dispatch(uploadVerificationDocumentsRequest(personal_id, trade_license));
    },
    [dispatch]
  );

  const onShowUploadVerificationDocumentsModal = useCallback(
    (isReUpload = false) => dispatch(showUploadVerificationDocumentsModal(isReUpload)),
    [dispatch]
  );

  const onHideUploadVerificationDocumentsModal = useCallback(
    () => dispatch(hideUploadVerificationDocumentsModal()),
    [dispatch]
  );

  const onClearError = useCallback(() => error && dispatch(clearError()), [error, dispatch]);

  return {
    user,
    isLoading,
    isAuthReady,
    error,
    isLoggedIn: !!user,
    onLogin,
    onEnterVerificationCode,
    onAutoLogin,
    onLogout,
    onResetPassword,
    onConfirmResetPassword,
    onConfirmRegistration,
    onClearError,
    isFirstLogin,
    onUploadVerificationDocuments,
    isUploadDocsVerificationShown,
    isReUploadDocs,
    onShowUploadVerificationDocumentsModal,
    onHideUploadVerificationDocumentsModal,
  };
};

export default useAuth;
